import { useEffect } from 'react';

interface UsePageKeyboardParams {
  enabled?: boolean;
  // Pressed Enter outside of a textarea / contenteditable.
  onEnter?: () => void;
  // Pressed Escape.
  onEscape?: () => void;
  // Pressed a number key 0–9. Surfaces map it to the active scale question's value.
  onNumberKey?: (key: number) => void;
  // Pressed a letter key A–J. Surfaces map index 0–9 to the active choice question's options.
  onLetterKey?: (index: number) => void;
}

// Centralised keyboard handler for survey surfaces. Replaces the per-component
// inline handlers so paged + conversational behave identically. Number/letter
// shortcuts are wired only when the surface knows there's a single active
// question (otherwise "1" is ambiguous between Q1's NPS and Q2's choice).
export function usePageKeyboard({
  enabled = true,
  onEnter,
  onEscape,
  onNumberKey,
  onLetterKey,
}: UsePageKeyboardParams): void {
  useEffect(() => {
    if (!enabled) return;
    const handler = (event: KeyboardEvent): void => {
      if (event.defaultPrevented) return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      const inTextField =
        tag === 'TEXTAREA' || target?.isContentEditable || (tag === 'INPUT' && isTextInput(target as HTMLInputElement));

      if (event.key === 'Escape') {
        onEscape?.();
        return;
      }

      if (event.key === 'Enter' && !event.shiftKey && !inTextField) {
        event.preventDefault();
        onEnter?.();
        return;
      }

      // Suppress shortcut keys while typing.
      if (inTextField) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      if (event.key.length === 1 && event.key >= '0' && event.key <= '9') {
        const num = Number(event.key);
        if (onNumberKey) {
          event.preventDefault();
          onNumberKey(num);
        }
        return;
      }

      if (event.key.length === 1 && /^[a-jA-J]$/.test(event.key)) {
        const index = event.key.toLowerCase().charCodeAt(0) - 'a'.charCodeAt(0);
        if (onLetterKey) {
          event.preventDefault();
          onLetterKey(index);
        }
        return;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [enabled, onEnter, onEscape, onNumberKey, onLetterKey]);
}

function isTextInput(input: HTMLInputElement): boolean {
  const t = input.type?.toLowerCase();
  return (
    !t ||
    t === 'text' ||
    t === 'email' ||
    t === 'password' ||
    t === 'number' ||
    t === 'search' ||
    t === 'tel' ||
    t === 'url'
  );
}
